import { useCallback, useEffect, useMemo, useState } from "react";
import { format, parseISO, addDays } from "date-fns";
import { it } from "date-fns/locale";
import { useNavigate } from "react-router-dom";
import {
  Bus,
  CalendarDays,
  MapPin,
  Plus,
  Route,
  Send,
  Printer,
  FileText,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { canAccessCentroDiurno } from "../../utils/centroDiurnoAccess";
import {
  getDashboard,
  getDisponibilita,
  getGiri,
  generaGiro,
  ottimizzaGiro,
  getMezzi,
} from "../../utils/centroDiurnoApi";
import PageHeader from "../../components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import CdRouteMap from "../../components/centro-diurno/CdRouteMap";
import CdAgendaGrid, { CdKpiCard, agendaRangeFromView, agendaWeekLabel } from "../../components/centro-diurno/CdAgendaGrid";

const FASCIA_GIRO_LABELS = { mattina: "Mattina", pomeriggio: "Pomeriggio" };
const STATO_GIRO_LABELS = { programmato: "Programmato", in_viaggio: "In viaggio", completato: "Completato" };

function giroGeneratoMessage(result) {
  const n = result?.fermate?.length ?? 0;
  const skipped = result?.skipped || [];
  if (n === 0 && skipped.length === 0) {
    return "Giro generato senza fermate: nessuna prenotazione nella fascia selezionata";
  }
  if (skipped.length === 0) {
    return `Giro generato con ${n} fermate`;
  }
  const noAddr = skipped.filter((s) => s.reason === "indirizzo_mancante").length;
  const parts = [`Giro con ${n} fermate`];
  if (noAddr) parts.push(`${noAddr} esclusi per indirizzo mancante`);
  return parts.join(" · ");
}

function latestGiriPerFascia(giri = []) {
  const byKey = {};
  for (const g of giri) {
    const key = `${g.fascia || "mattina"}:${g.direzione || "andata"}`;
    const prev = byKey[key];
    if (!prev || (g.created_at || "") >= (prev.created_at || "")) {
      byKey[key] = g;
    }
  }
  return Object.values(byKey).sort((a, b) => {
    const fa = `${a.fascia || ""}:${a.direzione || "andata"}`;
    const fb = `${b.fascia || ""}:${b.direzione || "andata"}`;
    return fa.localeCompare(fb);
  });
}

function formatDurata(sec) {
  if (!sec) return "—";
  const m = Math.round(sec / 60);
  const h = Math.floor(m / 60);
  const rm = m % 60;
  return h ? `${h}h ${rm}m` : `${rm}m`;
}

export default function CentroDiurnoDashboard() {
  const { token, user, hasPermission, activeStruttura } = useAuth();
  const navigate = useNavigate();
  const [dataSel, setDataSel] = useState(format(new Date(), "yyyy-MM-dd"));
  const [dash, setDash] = useState(null);
  const [agendaGiorni, setAgendaGiorni] = useState([]);
  const [agendaView, setAgendaView] = useState("settimana");
  const [agendaRefDate, setAgendaRefDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [giri, setGiri] = useState([]);
  const [fasciaGiro, setFasciaGiro] = useState("mattina");
  const [direzioneGiro, setDirezioneGiro] = useState("andata");
  const [loading, setLoading] = useState(true);

  const canManage = hasPermission("centro_diurno:manage");
  const agendaWeekCaption = useMemo(() => agendaWeekLabel(agendaRefDate), [agendaRefDate]);
  const giroSelezionato = useMemo(
    () =>
      giri.find(
        (g) => g.fascia === fasciaGiro && (g.direzione || "andata") === direzioneGiro
      ) || null,
    [giri, fasciaGiro, direzioneGiro]
  );
  const giriOggi = useMemo(() => latestGiriPerFascia(dash?.giri), [dash?.giri]);
  const fasciaGiroLabel = fasciaGiro === "pomeriggio" ? "Pomeriggio" : "Mattina";
  const direzioneGiroLabel = direzioneGiro === "rientro" ? "Rientro a casa" : "Andata";

  const load = useCallback(async () => {
    if (!token || !canAccessCentroDiurno(user)) return;
    setLoading(true);
    try {
      const [da, a] = agendaRangeFromView(agendaRefDate, agendaView);
      const [d, disp, giriRes] = await Promise.all([
        getDashboard(token, dataSel),
        getDisponibilita(token, da, a),
        getGiri(token, dataSel),
      ]);
      setDash(d);
      setAgendaGiorni(disp.giorni || []);
      setGiri(giriRes.giri || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore caricamento dashboard");
    } finally {
      setLoading(false);
    }
  }, [token, user, dataSel, agendaView, agendaRefDate, activeStruttura?.id]);

  useEffect(() => {
    load();
  }, [load]);

  const shiftAgendaWeek = (delta) => {
    setAgendaRefDate((prev) => format(addDays(parseISO(prev), delta * 7), "yyyy-MM-dd"));
  };

  if (!canAccessCentroDiurno(user)) {
    return (
      <div className="p-8 text-center text-slate-600">
        Modulo Centro Diurno non attivo. Contatta l&apos;amministratore Affidya.
      </div>
    );
  }

  const kpi = dash?.kpi || {};

  const handleGeneraGiro = async (fascia, direzione = "andata") => {
    try {
      const mezzi = await getMezzi(token);
      const m = mezzi.mezzi?.[0];
      if (!m) {
        toast.error("Configura almeno un mezzo in Mezzi e Autisti");
        navigate("/centro-diurno/mezzi");
        return;
      }
      const result = await generaGiro(token, { data: dataSel, fascia, direzione, mezzo_id: m.id });
      if (result?.fermate?.length) {
        toast.success(giroGeneratoMessage(result));
      } else {
        toast.warning(giroGeneratoMessage(result));
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore generazione giro");
    }
  };

  const handleOttimizza = async () => {
    if (!giroSelezionato?.id) return;
    try {
      await ottimizzaGiro(token, giroSelezionato.id);
      toast.success("Percorso ottimizzato");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Ottimizzazione non riuscita (verifica GOOGLE_MAPS_API_KEY)");
    }
  };

  return (
    <div className="min-h-screen bg-slate-100">
      <div className="p-4 md:p-6 lg:p-8 max-w-[1400px] mx-auto space-y-6">
        <PageHeader
          title="Centro Diurno"
          subtitle="Gestisci prenotazioni, presenze e trasporti"
          icon={Sparkles}
          iconContainerClassName="bg-gradient-to-br from-emerald-500 to-emerald-700"
          iconClassName="text-white"
        />

        <div className="flex flex-wrap items-center gap-3">
          <Input
            type="date"
            value={dataSel}
            onChange={(e) => {
              setDataSel(e.target.value);
              setAgendaRefDate(e.target.value);
            }}
            className="w-auto bg-white"
          />
          <span className="text-sm text-slate-600 capitalize">
            {format(parseISO(dataSel), "EEEE d MMMM yyyy", { locale: it })}
          </span>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <CdKpiCard label="Presenti oggi" value={kpi.presenti_oggi ?? "—"} sub={kpi.presenti_in_ritardo ? `di cui ${kpi.presenti_in_ritardo} in ritardo` : undefined} />
          <CdKpiCard label="Prenotati oggi" value={kpi.prenotati_oggi ?? "—"} sub={`Capienza ${kpi.capacita ?? 20} posti`} accent="blue" />
          <CdKpiCard label="Assenti" value={kpi.assenti_oggi ?? "—"} sub="comunicati oggi" accent="amber" />
          <CdKpiCard label="Posti disponibili" value={kpi.posti_disponibili ?? "—"} sub={`${kpi.percentuale_disponibilita ?? 0}% disponibilità`} />
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          <div className="min-w-0">
            <CdAgendaGrid
              giorni={agendaGiorni}
              capacita={kpi.capacita || 20}
              view={agendaView}
              onViewChange={setAgendaView}
              showViewToggle
              showWeekNav
              weekLabel={agendaWeekCaption}
              onPrevWeek={() => shiftAgendaWeek(-1)}
              onNextWeek={() => shiftAgendaWeek(1)}
            />
          </div>

          <Card className="shadow-sm border-slate-200">
            <CardHeader className="pb-2 space-y-3">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <Bus className="w-4 h-4 text-emerald-600" />
                Giro Pulmino — {fasciaGiroLabel} ({direzioneGiroLabel})
              </CardTitle>
              <Tabs
                value={fasciaGiro}
                onValueChange={(v) => {
                  setFasciaGiro(v);
                  setDirezioneGiro(v === "pomeriggio" ? "rientro" : "andata");
                }}
              >
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="mattina">Mattina 08:00-13:00</TabsTrigger>
                  <TabsTrigger value="pomeriggio">Pomeriggio 13:00-18:00</TabsTrigger>
                </TabsList>
              </Tabs>
              <Tabs value={direzioneGiro} onValueChange={setDirezioneGiro}>
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="andata">Andata — raccolta</TabsTrigger>
                  <TabsTrigger value="rientro">Rientro a casa</TabsTrigger>
                </TabsList>
              </Tabs>
            </CardHeader>
            <CardContent className="space-y-4">
              {giroSelezionato ? (
                <>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="rounded-lg bg-slate-50 p-3">
                      <p className="text-2xl font-bold text-slate-800">{giroSelezionato.fermate?.length || 0}</p>
                      <p className="text-xs text-slate-500">Ospiti</p>
                    </div>
                    <div className="rounded-lg bg-slate-50 p-3">
                      <p className="text-2xl font-bold text-slate-800">{Math.round((giroSelezionato.distanza_m || 0) / 1000)} km</p>
                      <p className="text-xs text-slate-500">Distanza stimata</p>
                    </div>
                    <div className="rounded-lg bg-slate-50 p-3">
                      <p className="text-2xl font-bold text-slate-800">{formatDurata(giroSelezionato.durata_s)}</p>
                      <p className="text-xs text-slate-500">Tempo stimato</p>
                    </div>
                  </div>
                  <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
                    Percorso ottimizzato Google Routes
                  </Badge>
                  <div className="flex flex-wrap gap-2">
                    {canManage && (
                      <Button variant="outline" size="sm" onClick={handleOttimizza}>
                        <Route className="w-4 h-4 mr-1" /> Ottimizza percorso
                      </Button>
                    )}
                    <Button size="sm" className="bg-[#4CAF50] hover:bg-[#43A047]" onClick={() => navigate("/centro-diurno/trasporti")}>
                      Apri dettaglio percorso
                    </Button>
                  </div>
                </>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm text-slate-500">
                    Nessun giro {direzioneGiroLabel.toLowerCase()} ({fasciaGiroLabel.toLowerCase()}) per questa data.
                  </p>
                  {canManage && (
                    <Button
                      size="sm"
                      className="bg-[#4CAF50] hover:bg-[#43A047]"
                      onClick={() => handleGeneraGiro(fasciaGiro, direzioneGiro)}
                    >
                      <Bus className="w-4 h-4 mr-1" /> Genera {direzioneGiroLabel.toLowerCase()}
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="shadow-sm border-slate-200 min-w-0">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold">Prossime Prenotazioni</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {(dash?.prossime_prenotazioni || []).length === 0 ? (
                <p className="text-sm text-slate-500">Nessuna prenotazione futura</p>
              ) : (
                dash.prossime_prenotazioni.map((p) => (
                  <div key={p.data} className="flex justify-between items-center py-2 border-b border-slate-100 last:border-0">
                    <span className="text-sm text-slate-700 capitalize">
                      {format(parseISO(p.data), "EEE d MMMM", { locale: it })}
                    </span>
                    <Badge variant="secondary">{p.prenotati} prenotati</Badge>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          <Card className="shadow-sm border-slate-200 min-w-0">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <MapPin className="w-4 h-4 text-blue-600 shrink-0" />
                <span className="leading-tight">
                  Percorso di Oggi — {fasciaGiroLabel} ({direzioneGiroLabel})
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <CdRouteMap
                fermate={giroSelezionato?.fermate || []}
                polyline={giroSelezionato?.polyline}
                sede={giroSelezionato?.struttura_sede}
                height="220px"
              />
            </CardContent>
          </Card>

          <Card className="shadow-sm border-slate-200 min-w-0">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold">Stato Pulmini</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {(giriOggi.length === 0 && (dash?.mezzi || []).length === 0) ? (
                <p className="text-sm text-slate-500">Nessun mezzo configurato</p>
              ) : (
                (giriOggi.length ? giriOggi : dash?.mezzi || []).map((item, i) => {
                  const statoKey = item.stato || "programmato";
                  const statoLabel = STATO_GIRO_LABELS[statoKey] || statoKey;
                  const fasciaLabel = FASCIA_GIRO_LABELS[item.fascia] || item.fascia;
                  const dirLabel = (item.direzione || "andata") === "rientro" ? "Rientro" : "Andata";
                  return (
                    <div
                      key={item.id || `${item.fascia}-${item.direzione}-${i}`}
                      className="flex flex-wrap items-center justify-between gap-2 p-3 rounded-lg bg-slate-50"
                    >
                      <div className="min-w-0">
                        <p className="font-medium text-slate-800 text-sm">
                          {item.mezzo_nome || item.nome || `Pulmino ${i + 1}`}
                          {item.fascia ? ` · ${fasciaLabel} · ${dirLabel}` : ""}
                        </p>
                        <p className="text-xs text-slate-500">{statoLabel} · {item.fermate?.length || 0} ospiti</p>
                      </div>
                      <Badge className={`shrink-0 ${statoKey === "in_viaggio" ? "bg-emerald-600" : "bg-slate-500"}`}>
                        {statoLabel}
                      </Badge>
                    </div>
                  );
                })
              )}
            </CardContent>
          </Card>
        </div>

        <Card className="shadow-sm border-emerald-100 bg-gradient-to-r from-emerald-50/80 to-white">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-emerald-900">Azioni Rapide</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate("/centro-diurno/agenda")}>
              <Plus className="w-4 h-4 mr-1" /> Prenota ospite
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate("/centro-diurno/presenze")}>
              <Printer className="w-4 h-4 mr-1" /> Presenze di oggi
            </Button>
            {canManage && (
              <>
                <Button variant="outline" size="sm" onClick={() => handleGeneraGiro("mattina", "andata")}>
                  <Bus className="w-4 h-4 mr-1" /> Andata mattina
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleGeneraGiro("pomeriggio", "andata")}>
                  <Bus className="w-4 h-4 mr-1" /> Andata pomeriggio
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleGeneraGiro("mattina", "rientro")}>
                  <Bus className="w-4 h-4 mr-1" /> Rientro mattina
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleGeneraGiro("pomeriggio", "rientro")}>
                  <Bus className="w-4 h-4 mr-1" /> Rientro pomeriggio
                </Button>
              </>
            )}
            <Button variant="outline" size="sm" onClick={() => window.open("/autista-login", "_blank")}>
              <Send className="w-4 h-4 mr-1" /> Area autista
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate("/centro-diurno/report")}>
              <FileText className="w-4 h-4 mr-1" /> Report giornaliero
            </Button>
          </CardContent>
        </Card>

        {loading && <p className="text-center text-sm text-slate-400">Aggiornamento...</p>}
      </div>
    </div>
  );
}
